import { Clock3, Info, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

import { FormField } from '@/frontend/components/form/fields'
import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { RichTextContent } from '@/frontend/components/ui/rich-text-content'
import { Textarea } from '@/frontend/components/ui/textarea'
import { useGetPatientSummary } from '@/frontend/hooks/api/patients/use-get-patient-summary'
import { useGetPatientSummaryReview } from '@/frontend/hooks/api/patients/use-get-patient-summary-review'
import { usePostPatientSummaryReview } from '@/frontend/hooks/api/patients/use-post-patient-summary-review'
import { PetriLogNotFoundError } from '@/frontend/hooks/api/use-petri-api'
import { cn } from '@/frontend/lib/utils'

type Summary = NonNullable<ReturnType<typeof useGetPatientSummary>['data']>

const FEEDBACK_EXAMPLES = {
  up: [
    'It caught the missed Gabapentin doses and the difficulty getting the cat to the clinic.',
    'Flagged an unintentional Apoquel dosage inconsistency across the last 3 dispenses.',
  ],
  down: [
    'The summary says the member has Felisecto and Quantex at home, but the 25 July 2025 consult only authorises future medication.',
    'Describing the owner as "extremely anxious, cost-conscious, needle-averse" is too strong for a screen members may see.',
  ],
}

/** The displayed generation date uses the clinical request time to indicate the summary's source freshness. */
export function PatientSummarySection({ patientId }: { patientId: string }) {
  const { data: summary } = useGetPatientSummary(patientId)
  if (!summary?.content) return null

  return <PatientSummaryCard summary={summary} />
}

function PatientSummaryCard({ summary }: { summary: Summary }) {
  const [open, setOpen] = useState(false)
  const [rating, setRating] = useState<'up' | 'down'>('down')
  const [comment, setComment] = useState<string | null>(null)
  const reviewQuery = useGetPatientSummaryReview({ externalId: summary.generationId })
  const { data: existingReview } = reviewQuery
  const reviewAvailable =
    !reviewQuery.isPending && !(reviewQuery.error instanceof PetriLogNotFoundError)
  const review = usePostPatientSummaryReview()

  function openFeedback(next: 'up' | 'down') {
    const nextRating = next === 'up' ? 'pass' : 'fail'
    setRating(next)
    setComment(existingReview?.rating === nextRating ? existingReview.comment : null)
    setOpen(true)
  }

  function closeFeedback() {
    setOpen(false)
    setComment(null)
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!summary.generationId) return

    const trimmed = comment?.trim()
    if (rating === 'down' && !trimmed) return

    review.mutate(
      {
        externalId: summary.generationId,
        rating: rating === 'up' ? 'pass' : 'fail',
        comment: trimmed || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Thanks, review sent')
          closeFeedback()
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to send review'),
      }
    )
  }

  return (
    <section className="min-w-0 rounded-lg border border-ai-border bg-card p-0.5">
      <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
        <Sparkles className="size-3.5 text-ai-fg" />
        <h2>AI Summary</h2>
        <Badge variant="neutral" size="xs">
          Experimental
        </Badge>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {summary.stale && (
            <Badge variant="pending" size="xs">
              <Clock3 aria-hidden="true" />
              Stale
            </Badge>
          )}
          <span className="text-2xs font-normal">
            Generated <FormattedDateTime value={summary.requestedAt} clinicId={null} />
          </span>
          {summary.generationId && reviewAvailable && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Helpful summary"
                title="Helpful"
                onClick={() => openFeedback('up')}
              >
                <ThumbsUp
                  className={cn(
                    existingReview?.rating === 'pass' && 'fill-current text-success-accent'
                  )}
                />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Unhelpful summary"
                title="Not helpful"
                onClick={() => openFeedback('down')}
              >
                <ThumbsDown
                  className={cn(
                    existingReview?.rating === 'fail' && 'fill-current text-destructive-accent'
                  )}
                />
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="px-2.5 pt-0.5">
        <ExpandableContent
          maxHeight={100}
          footer={
            <p className="flex items-start justify-end gap-1 text-right text-xs text-warning-accent">
              <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              <span className="font-medium">
                AI can invent, exaggerate, or miss important details. Always check the patient
                record before acting.
              </span>
            </p>
          }
        >
          <RichTextContent html={summary.content} className="select-text break-words" />
        </ExpandableContent>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && closeFeedback()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rating === 'up' ? 'What was useful?' : 'What could be better?'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <p className="mb-2 text-sm text-muted-foreground">
                Specific feedback helps us keep what works and fix what doesn't. Reference the exact
                clinical record where you can (e.g. Dr Bob's 25 July 2025 routine checkup).
              </p>
              <p className="text-xs font-medium">Examples</p>
              <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                {FEEDBACK_EXAMPLES[rating].map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </div>
            <FormField
              label={rating === 'up' ? 'Feedback (optional)' : 'Feedback'}
              required={rating === 'down'}
              render={({ id, disabled }) => (
                <Textarea
                  id={id}
                  value={comment ?? ''}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={
                    rating === 'up' ? 'Anything else worth adding?' : 'What was wrong or missing?'
                  }
                  maxLength={2000}
                  disabled={disabled}
                  required={rating === 'down'}
                  autoFocus
                />
              )}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeFeedback}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={review.isPending || (rating === 'down' && !comment?.trim())}
              >
                Send feedback
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
